import React, { useRef, useState, CSSProperties } from 'react';
import { Popover, List, ListItemButton } from '@mui/material';
import styled from 'styled-components';
import cx from 'classnames';
import DropDownArrow from '../Icons/DropDownArrow';

const SelectRoot = styled.div`
    width: 100%;
    color: rgba(0, 0, 0, 0.87);
    cursor: pointer;
    display: inline-flex;
    justify-content: space-between;
    position: relative;
    font-size: 1rem;
    box-sizing: border-box;
    align-items: center;
    line-height: 1.25rem;
    padding: 1.125rem 0.875rem;
    border: 2px solid #c2cbd4;
    border-radius: 6px;

    &.selectSizeSmall {
        line-height: 1rem;
        padding: 0.725rem 0.875rem;
    }
    &.selectDisabled {
        cursor: not-allowed;
        background-color: #eff3f8;
    }
    &.hasError {
        border: 2px solid #df352c;
    }

    .selectInput {
        display: none;
    }
    .selectValue {
        text-overflow: ellipsis;
        flex-grow: 1;
        white-space: nowrap;
        overflow: hidden;
        pointer-events: none;
    }
    .downArrow {
        width: 16px;
        height: 16px;
        transition: transform 300ms ease;
        margin-left: 12px;

        &.focused {
            transform: rotateX(-180deg);
        }
    }
`;

const StyledPopover = styled(Popover)`
    width: 100%;
    top: 5px !important;
    z-index: 1550;
`;

const StyledListItemButton = styled(ListItemButton)`
    background-color: #fff;
    font-size: 1rem;

    &:hover {
        background-color: #d8dde2 !important;
    }
    &.Mui-selected {
        background-color: #d8dde2 !important;
    }
`;

export interface SelectProps<T> {
    value: T | null;
    options: T[];
    getOptionLabel: (option: T) => string;
    getOptionValue: (option: T) => string;
    onChange?: (value: T) => void;
    className?: string;
    style?: CSSProperties;
    disabled?: boolean;
    id?: string;
    name?: string;
    inputRef?: React.Ref<any>;
    error?: boolean;
    size?: 'small' | 'medium';
}

function Select<T>({
    options,
    value,
    getOptionLabel,
    getOptionValue,
    onChange,
    className = '',
    style,
    disabled = false,
    inputRef,
    name,
    error,
    size = 'medium',
    ...props
}: SelectProps<T>) {
    const divRef = useRef<HTMLDivElement | null>(null);
    const [dropdownOpen, setDropDownOpen] = useState(false);
    const { id } = props;

    const toggleDropdown = () => {
        if (!disabled) {
            setDropDownOpen(!dropdownOpen);
        }
    };

    const onSelect = (option: T | undefined) => {
        setDropDownOpen(false);
        if (option && (!value || getOptionValue(value) !== getOptionValue(option))) {
            onChange?.(option);
        }
    };

    const onClose = () => {
        setDropDownOpen(false);
    };

    return (
        <>
            <SelectRoot
                ref={divRef}
                onClick={toggleDropdown}
                className={cx(className, {
                    selectDisabled: disabled,
                    hasError: error,
                    selectSizeSmall: size === 'small',
                })}
                style={style}
                {...props}
            >
                <input
                    type="text"
                    className="selectInput"
                    value={value ? getOptionValue(value) : ''}
                    name={name}
                    ref={inputRef}
                    readOnly
                />
                <div className="selectValue">{value && getOptionLabel(value)}</div>
                <DropDownArrow className={cx('downArrow', { focused: dropdownOpen })} />
            </SelectRoot>
            {dropdownOpen && divRef.current && (
                <StyledPopover
                    open
                    anchorEl={divRef.current}
                    style={{
                        width:
                            (divRef?.current?.clientWidth ? divRef?.current?.clientWidth + 4 : 0) +
                            30,
                    }}
                    role="presentation"
                    onClose={onClose}
                    PaperProps={{
                        style: { width: '100%' },
                        elevation: 2,
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <List component="nav">
                        {options.map((option: T) => {
                            return (
                                <StyledListItemButton
                                    selected={
                                        !!value && getOptionValue(value) === getOptionValue(option)
                                    }
                                    key={getOptionValue(option)}
                                    onClick={() => onSelect(option)}
                                    id={`${id}-option-${getOptionValue(option)}`}
                                >
                                    {getOptionLabel(option)}
                                </StyledListItemButton>
                            );
                        })}
                    </List>
                </StyledPopover>
            )}
        </>
    );
}

export default Select;
